import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Plus, Trash2, RefreshCw, CheckCircle } from 'lucide-react';
import { initializeFormeRecente, syncMatchsFromAPI } from '../../utils/formeRecenteManager';

const AdminFormeRecente = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [matchs, setMatchs] = useState([
    {
      date: '2025-09-28T00:00:00.000Z',
      resultat: 'V',
      adversaire: 'LA MADELEINE FC',
      score: '0-2',
      domicile: 'Extérieur',
      competition: 'COUPE DE FRANCE CRÉDIT AGRICOLE'
    },
    {
      date: '',
      resultat: 'V',
      adversaire: '',
      score: '',
      domicile: 'Domicile',
      competition: 'N2'
    },
    {
      date: '',
      resultat: 'N',
      adversaire: '',
      score: '',
      domicile: 'Domicile',
      competition: 'N2'
    },
    {
      date: '',
      resultat: 'D',
      adversaire: '',
      score: '',
      domicile: 'Extérieur',
      competition: 'N2'
    },
    {
      date: '',
      resultat: 'V',
      adversaire: '',
      score: '',
      domicile: 'Domicile',
      competition: 'N2'
    },
  ]);

  const handleMatchChange = (index, field, value) => {
    const newMatchs = [...matchs];
    newMatchs[index][field] = value;
    setMatchs(newMatchs);
  };

  const handleInitialize = async () => {
    try {
      setLoading(true);
      setMessage('');

      // Filtrer les matchs vides
      const validMatchs = matchs.filter(m => m.date && m.adversaire && m.score);

      if (validMatchs.length === 0) {
        setMessage('❌ Aucun match valide à initialiser. Remplis au moins un match complet.');
        return;
      }

      const success = await initializeFormeRecente(validMatchs);

      if (success) {
        setMessage(`✅ Initialisation réussie ! ${validMatchs.length} match(s) ajouté(s) à Firestore.`);
      } else {
        setMessage('❌ Échec de l\'initialisation. Vérifie la console pour plus de détails.');
      }
    } catch (error) {
      console.error('Erreur initialisation:', error);
      setMessage(`❌ Erreur : ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      setMessage('');

      const count = await syncMatchsFromAPI();

      if (count > 0) {
        setMessage(`✅ Synchronisation réussie ! ${count} nouveau(x) match(s) ajouté(s).`);
      } else {
        setMessage('ℹ️ Aucun nouveau match détecté depuis l\'API FFF.');
      }
    } catch (error) {
      console.error('Erreur sync:', error);
      setMessage(`❌ Erreur : ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002B76] to-[#001B56] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-title font-bold text-white mb-2 flex items-center gap-3">
            <TrendingUp className="text-[#FFE400]" size={40} />
            Gestion Forme Récente N2
          </h1>
          <p className="text-[#FFE400]/80 font-accent">
            Initialise et gère les 5 derniers matchs de l'équipe Senior N2
          </p>
        </motion.div>

        {/* Boutons actions */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={handleInitialize}
            disabled={loading}
            className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-6 py-3 rounded-lg font-title font-bold flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={20} />
            Initialiser Firestore
          </button>

          <button
            onClick={handleSync}
            disabled={loading}
            className="bg-white/10 backdrop-blur-sm border border-[#FFE400]/30 text-white px-6 py-3 rounded-lg font-title font-bold flex items-center gap-2 hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            Synchroniser avec API
          </button>
        </div>

        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 p-4 rounded-lg font-accent ${
              message.startsWith('✅')
                ? 'bg-green-500/20 border border-green-500/50 text-green-300'
                : message.startsWith('❌')
                ? 'bg-red-500/20 border border-red-500/50 text-red-300'
                : 'bg-blue-500/20 border border-blue-500/50 text-blue-300'
            }`}
          >
            {message}
          </motion.div>
        )}

        {/* Formulaire matchs */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#FFE400]/20">
          <h2 className="text-2xl font-title font-bold text-white mb-6">
            5 Derniers Matchs (du plus récent au plus ancien)
          </h2>

          <div className="space-y-6">
            {matchs.map((match, index) => (
              <div
                key={index}
                className="bg-white/5 border border-[#FFE400]/10 rounded-lg p-4 grid grid-cols-1 md:grid-cols-6 gap-4"
              >
                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={match.date.split('T')[0]}
                    onChange={(e) => handleMatchChange(index, 'date', e.target.value + 'T00:00:00.000Z')}
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white focus:outline-none focus:border-[#FFE400]"
                  />
                </div>

                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Résultat
                  </label>
                  <select
                    value={match.resultat}
                    onChange={(e) => handleMatchChange(index, 'resultat', e.target.value)}
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white focus:outline-none focus:border-[#FFE400]"
                  >
                    <option value="V">Victoire</option>
                    <option value="N">Nul</option>
                    <option value="D">Défaite</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Adversaire
                  </label>
                  <input
                    type="text"
                    value={match.adversaire}
                    onChange={(e) => handleMatchChange(index, 'adversaire', e.target.value)}
                    placeholder="Ex: BOURGES FC"
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:border-[#FFE400]"
                  />
                </div>

                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Score
                  </label>
                  <input
                    type="text"
                    value={match.score}
                    onChange={(e) => handleMatchChange(index, 'score', e.target.value)}
                    placeholder="Ex: 2-1"
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:border-[#FFE400]"
                  />
                </div>

                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Lieu
                  </label>
                  <select
                    value={match.domicile}
                    onChange={(e) => handleMatchChange(index, 'domicile', e.target.value)}
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white focus:outline-none focus:border-[#FFE400]"
                  >
                    <option value="Domicile">Domicile</option>
                    <option value="Extérieur">Extérieur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#FFE400] text-sm font-accent mb-2">
                    Compétition
                  </label>
                  <input
                    type="text"
                    value={match.competition}
                    onChange={(e) => handleMatchChange(index, 'competition', e.target.value)}
                    placeholder="Ex: N2"
                    className="w-full bg-white/10 border border-[#FFE400]/30 rounded px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:border-[#FFE400]"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-blue-300 font-accent text-sm">
              💡 <strong>Instructions :</strong>
            </p>
            <ul className="text-blue-300 font-accent text-sm mt-2 space-y-1 list-disc list-inside">
              <li>Remplis les 5 derniers matchs (du plus récent au plus ancien)</li>
              <li>Clique sur "Initialiser Firestore" pour sauvegarder</li>
              <li>Par la suite, utilise "Synchroniser avec API" pour ajouter automatiquement les nouveaux matchs</li>
              <li>Seuls les matchs complets (avec date, adversaire et score) seront sauvegardés</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFormeRecente;

